import { Emitter } from './utils/emitter';
import { config, isOriginAllowed, getCheckoutOrigin } from './config';
import { ThreeDSModalController, type ThreeDSFlowData } from './threeds-modal';

import {
  PaypercutCheckoutOptions,
  CheckoutInstance,
  PaypercutCheckoutStatic,
  EventName,
  SdkEvent,
  validatePaymentMethods,
  validateWalletOptions,
  validateUIMode,
  UIMode,
  WalletType,
  FormValidationError,
  Appearance,
  AppearanceVariables,
  ApplePayBridgeMsg,
} from './types';
import { normalizeLocale } from './types';
import { APPLE_PAY_BRIDGE_POLICY, selectApplePaySessionVersion } from './apple-pay-policy';

// Re-export types and enums for consumers
export type {
  PaypercutCheckoutOptions,
  CheckoutInstance,
  PaypercutCheckoutStatic,
  EventName,
  WalletType,
  CancelPayload,
  FormValidationError,
  Appearance,
  AppearanceVariables,
} from './types';

export { UIMode, PaymentMethod, WalletOption } from './types/checkout';

// Re-export SDK event enums for consumers
export { SdkEvent } from './types/checkout';

export type { Locale } from './types/locales';
export { LOCALES } from './types/locales';

// Express checkout
export { PaypercutExpressCheckout } from './express';
export type {
  ExpressCheckoutOptions,
  ExpressCheckoutInstance,
  LineItem,
  ExpressCheckoutShippingOption,
  ExpressCheckoutUpdateOptions,
  ExpressEventName,
  ExpressShippingAddress,
  ExpressSessionStartEvent,
  ShippingAddressChangeEvent,
  ShippingOptionChangeEvent,
  ExpressTokenPayload,
  ExpressBillingDetails,
  ExpressShippingDetails,
  ExpressPaymentMethodCreatedEvent,
  ExpressPaymentMethodFailedEvent,
  ExpressButtonOptions,
  ExpressButtonType,
  ApplePayButtonStyle,
  GooglePayButtonColor,
} from './types/express';
export { ExpressSdkEvent } from './types/express';

// ── Apple Pay SDK loader ──────────────────────────────────────────────────────

let _applePaySdkInjected = false;

/**
 * Injects Apple's cross-browser Apple Pay JS SDK into the parent page so that
 * window.ApplePaySession is available when the embedded bridge delegates session
 * creation here. On Safari, ApplePaySession is a browser built-in and the script
 * is a no-op; on Chrome it provides the iCloud Keychain integration.
 */
function ensureApplePaySdk(): void {
  if (typeof window === 'undefined') return;
  if ((window as any).ApplePaySession) return;
  if (_applePaySdkInjected) return;
  _applePaySdkInjected = true;
  const s = document.createElement('script');
  s.src = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
  s.crossOrigin = 'anonymous';
  s.async = true;
  document.head.appendChild(s);
}

// Deferred Elements
export { Paypercut } from './paypercut';
export type {
  PaypercutOptions,
  PaypercutElementsMode,
  PaypercutElementsAppearanceUpdates,
  PaypercutElementsOptions,
  PaypercutElementsAppearance,
  PaypercutElementsAppearanceVariableName,
  PaypercutElementsAppearanceVariables,
  PaypercutElementsAppearanceRuleSelector,
  PaypercutElementsAppearanceRuleProperty,
  PaypercutElementsAppearanceRules,
  PaypercutElementsUpdateOptions,
  PaypercutInstance,
  PaypercutStatic,
  PaypercutElements,
  CardElement,
  CardElementEventName,
  ElementsResizeEvent,
  ElementsErrorEvent,
  ElementsErrorCode,
  KnownElementsErrorCode,
  ElementsSubmitError,
  DeferredPaymentReference,
  PaypercutBillingAddress,
  PaypercutBillingDetails,
  PaypercutCreatePaymentMethodParams,
  PaypercutSetupFutureUsage,
  PaypercutCreatePaymentMethodOptions,
  PaypercutConfirmPaymentOptions,
  PaypercutConfirmPaymentResult,
  CardFieldElementType,
  CardFieldElementEventName,
  CardFieldChangeEvent,
  CardFieldElement,
  CardNumberElementOptions,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  ExpressCheckoutElement,
  ExpressCheckoutElementOptions,
  ExpressCheckoutElementEventName,
  ExpressCheckoutWallet,
  ExpressCheckoutPaymentMethodVisibility,
  ExpressCheckoutAvailablePaymentMethodsChangeEvent,
  ExpressCheckoutClickEvent,
  ExpressCheckoutConfirmEvent,
  ExpressCheckoutCancelEvent,
  ExpressCheckoutLineItem,
  ExpressCheckoutAddress,
  ExpressCheckoutBillingDetails,
} from './types/elements';

// ── Appearance validation ─────────────────────────────────────────────────────

const _HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const _CSS_LEN_RE = /^(0|(\d+(\.\d+)?(px|rem|em)))$/;

function warnInvalidAppearanceVariables(variables: AppearanceVariables | undefined): void {
  if (!variables) return;

  if (variables.brandColor !== undefined && !_HEX_RE.test(variables.brandColor)) {
    console.warn(
      `[PaypercutCheckout] Invalid appearance.variables.brandColor: "${variables.brandColor}". ` +
        `Expected a hex color (#RGB or #RRGGBB). Value ignored.`,
    );
  }

  if (variables.fontFamily !== undefined && !variables.fontFamily.trim()) {
    console.warn(
      `[PaypercutCheckout] Invalid appearance.variables.fontFamily: value is empty. Value ignored.`,
    );
  }

  if (variables.logoUrl !== undefined && !/^https:\/\//.test(variables.logoUrl)) {
    console.warn(
      `[PaypercutCheckout] Invalid appearance.variables.logoUrl: "${variables.logoUrl}". ` +
        `Must start with https://. Value ignored.`,
    );
  }

  if (variables.borderRadius !== undefined && !_CSS_LEN_RE.test(variables.borderRadius.trim())) {
    console.warn(
      `[PaypercutCheckout] Invalid appearance.variables.borderRadius: "${variables.borderRadius}". ` +
        `Expected a CSS length like "8px", "0.5rem", or "0". Value ignored.`,
    );
  }
}

// ── Checkout implementation ───────────────────────────────────────────────────

/**
 * Internal implementation of CheckoutInstance
 */
class CheckoutImpl implements CheckoutInstance {
  private emitter = new Emitter<EventName>();
  private mounted = false;
  private destroyed = false;
  private iframe: HTMLIFrameElement | null = null;
  private messageHandler: (evt: MessageEvent) => void;
  private threeDSModal: ThreeDSModalController;

  // Apple Pay bridge state (embedded mode only)
  private applePaySession: any = null;
  private applePayBridgeTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(private options: PaypercutCheckoutOptions) {
    // Bind message handler
    this.messageHandler = this.onMessage.bind(this);
    this.threeDSModal = new ThreeDSModalController({
      modalId: 'paypercut-3ds-modal',
      buildUrl: (data) => this.buildThreeDSUrl(data),
      onCancel: () => this.handle3DSShellCancel(),
      onReady: (iframe) => this.postThreeDSInit(iframe),
    });
    window.addEventListener('message', this.messageHandler);

    // In embedded mode the Apple Pay bridge delegates ApplePaySession creation to this parent window.
    if (validateUIMode(options.ui_mode ?? UIMode.EMBEDDED) === UIMode.EMBEDDED) {
      ensureApplePaySdk();
    }
  }

  /**
   * Build the iframe source URL with query parameters
   */
  private buildSrc(): string {
    const baseUrl = getCheckoutOrigin(this.options.hostedCheckoutUrl);
    const url = new URL(`/c/${this.options.id}`, baseUrl);

    // Add locale parameter
    if (this.options.locale) {
      const normalizedLocale = normalizeLocale(this.options.locale);
      url.searchParams.set('locale', normalizedLocale);
    }

    // Add lang parameter
    if (this.options.lang) {
      const normalizedLocale = normalizeLocale(this.options.lang);
      url.searchParams.set('lang', normalizedLocale);
    }

    // Add ui_mode parameter (default to 'embedded')
    {
      const selected = validateUIMode(this.options.ui_mode ?? UIMode.EMBEDDED);
      if (selected) {
        url.searchParams.set('ui_mode', selected);
      }
    }

    // Add payment_methods parameters (repeated for each method)
    if (this.options.payment_methods && this.options.payment_methods.length > 0) {
      const validatedMethods = validatePaymentMethods(this.options.payment_methods as string[]);
      validatedMethods.forEach((method) => {
        url.searchParams.append('payment_methods', method);
      });
    }

    // Add wallet_options parameters (repeated for each wallet)
    // If wallet_options is explicitly set (even as empty array), we need to pass it
    if (this.options.wallet_options !== undefined) {
      if (this.options.wallet_options.length === 0) {
        // Explicit empty array means "no wallets" - pass null (coerced to "null" string)
        url.searchParams.set('wallet_options', null as unknown as string);
      } else {
        const validatedWallets = validateWalletOptions(this.options.wallet_options as string[]);
        validatedWallets.forEach((wallet) => {
          url.searchParams.append('wallet_options', wallet);
        });
      }
    }

    if (this.options.appearance?.preset) {
      url.searchParams.set('preset', this.options.appearance.preset);
    }

    if (this.options.appearance?.theme) {
      url.searchParams.set('theme', this.options.appearance.theme);
    }

    if (
      this.options.appearance?.variables &&
      Object.keys(this.options.appearance.variables).length > 0
    ) {
      warnInvalidAppearanceVariables(this.options.appearance.variables);
      url.searchParams.set('appearance_vars', JSON.stringify(this.options.appearance.variables));
    }

    if (this.options.form_only !== undefined) {
      url.searchParams.set('form_only', String(this.options.form_only));
    }

    // We send validate_form (Default to TRUE) if provided during the SDK intialization
    // If not provided we dont pass it for backward compatibility
    if (this.options.validate_form !== undefined) {
      url.searchParams.set('validate_form', String(this.options.validate_form));
    }

    return url.toString();
  }

  /**
   * Get the container element from selector or HTMLElement
   */
  private getContainer(): HTMLElement {
    const container =
      typeof this.options.containerId === 'string'
        ? document.querySelector(this.options.containerId)
        : this.options.containerId;

    if (!container) {
      throw new Error(`Container not found: ${this.options.containerId}`);
    }

    return container as HTMLElement;
  }

  /**
   * Handle incoming postMessage events from iframe
   */
  private onMessage(evt: MessageEvent): void {
    // Validate origin against allowed origins (build-time whitelist)
    if (!isOriginAllowed(evt.origin)) {
      return;
    }

    // Validate structure
    const data = evt.data;
    if (!data || typeof data !== 'object' || !('type' in data)) {
      return;
    }

    const messageFromCheckout = Boolean(
      this.iframe?.contentWindow && evt.source === this.iframe.contentWindow,
    );
    const messageFromThreeDS = this.threeDSModal.ownsMessageSource(evt.source);
    const threeDSFrameMessages = new Set([
      'PAYPERCUT_THREEDS_FRAME_RESIZE',
      'THREEDS_READY',
      'THREEDS_COMPLETE',
      'THREEDS_CANCELED',
      'THREEDS_ERROR',
    ]);
    if (
      (threeDSFrameMessages.has(data.type) && !messageFromThreeDS) ||
      (!threeDSFrameMessages.has(data.type) && !messageFromCheckout)
    ) {
      return;
    }

    // Filter messages by checkout session ID to prevent cross-instance message handling
    // This ensures each checkout instance only processes its own messages
    if (data.checkoutId && data.checkoutId !== this.options.id) {
      return; // Message is for a different checkout instance
    }

    // Handle specific events
    switch (data.type) {
      case 'CHECKOUT_LOADED':
        this.emitter.emit(SdkEvent.Loaded);
        // Declare bridge capabilities to the iframe so it can gate the Apple Pay
        // bridge on SDK support. Old SDKs never send this, so the iframe falls
        // back to native Apple Pay (same behaviour as before the bridge existed).
        this.postToIframe({
          type: ApplePayBridgeMsg.CAPABILITIES,
          checkoutId: this.options.id,
          applePayBridge: true,
        });
        break;
      case 'CHECKOUT_PROCESSING':
        this.emitter.emit(SdkEvent.Processing, {
          checkoutId: data.checkoutId,
        });
        break;
      case 'CHECKOUT_SUCCESS':
        const { payment_method = {}, payment = null } = data;
        this.emitter.emit(SdkEvent.Success, { payment_method, payment });
        break;
      case 'CHECKOUT_ERROR':
        // Forward error payload (if provided) to SDK consumers
        this.emitter.emit(SdkEvent.Error, (data && (data as any).error) ?? data);
        break;
      case 'CHECKOUT_CANCEL':
        this.emitter.emit(SdkEvent.Cancel, {
          checkoutId: data.checkoutId,
          wallet: (data as any).wallet as WalletType | undefined,
        });
        break;
      case 'CHECKOUT_EXPIRED':
        this.emitter.emit(SdkEvent.Expired);
        break;
      case 'CHECKOUT_RESIZE':
        this.emitter.emit(SdkEvent.Resize, data.height);
        this.handleResize(data.height);
        break;
      case 'PAYPERCUT_THREEDS_FRAME_RESIZE':
        this.threeDSModal.handleFrameResize(evt, data);
        break;
      case 'THREEDS_START_FLOW':
        this.threeDSModal.show(data);
        this.emitter.emit(SdkEvent.ThreeDSStarted);
        break;
      case 'THREEDS_READY':
        this.handle3DSReady();
        break;
      case 'THREEDS_COMPLETE':
        this.postToIframe(data);
        this.threeDSModal.close();
        this.emitter.emit(SdkEvent.ThreeDSComplete);
        break;
      case 'THREEDS_CANCELED':
        this.postToIframe(data);
        this.threeDSModal.close();
        this.emitter.emit(SdkEvent.ThreeDSCanceled);
        break;
      case 'THREEDS_ERROR':
        this.postToIframe(data);
        this.threeDSModal.close();
        this.emitter.emit(SdkEvent.ThreeDSError, data.error);
        break;
      case 'FORM_VALIDATION':
        // Wallet button clicked - emit event for merchant to validate their form
        this.emitter.emit(SdkEvent.FormValidation, {
          checkoutId: data.checkoutId,
          wallet: data.wallet as WalletType,
        });
        break;

      case ApplePayBridgeMsg.START:
        this.handleApplePayStart(data);
        break;
      case ApplePayBridgeMsg.MERCHANT_SESSION:
        this.handleApplePayMerchantSession(data.merchantSession);
        break;
      case ApplePayBridgeMsg.MERCHANT_ERROR:
        this.abortApplePaySession();
        break;
      case ApplePayBridgeMsg.PAYMENT_RESULT:
        this.handleApplePayPaymentResult(data.success === true);
        break;
    }
  }

  /**
   * Render the checkout iframe into the container
   */
  render(): void {
    if (this.mounted) {
      return;
    }

    try {
      const container = this.getContainer();

      // Create iframe
      this.iframe = document.createElement('iframe');
      this.iframe.id = 'paypercut-checkout-iframe';
      this.iframe.src = this.buildSrc();
      this.iframe.allow = 'payment *; clipboard-write';
      this.iframe.setAttribute('frameborder', '0');
      this.iframe.setAttribute('scrolling', 'no');
      // Allow Payment Request API inside iframe and necessary sandbox permissions for wallets
      this.iframe.setAttribute('allowpaymentrequest', 'true');
      this.iframe.setAttribute(
        'sandbox',
        'allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox',
      );

      // Apply default styles - just construct URL and assign to iframe
      Object.assign(this.iframe.style, {
        width: '100%',
        minWidth: '100%',
        height: '100%',
        border: 'none',
        display: 'block',
        overflow: 'hidden',
      });

      // Listen for iframe load event (fallback)
      // This ensures 'loaded' event is always emitted even if hosted checkout
      // doesn't send CHECKOUT_LOADED message
      /*this.iframe.addEventListener('load', () => {
        this.emitter.emit('loaded');
      });*/

      container.appendChild(this.iframe);
      this.mounted = true;
    } catch (err) {
      console.error('[PaypercutCheckout] Failed to render:', err);
      throw err;
    }
  }

  /**
   * Submit payment - sends message to hosted checkout to confirm payment
   */
  submit(): void {
    if (!this.mounted) {
      return;
    }

    try {
      this.postToIframe({
        type: 'START_PROCESSING',
        checkoutId: this.options.id,
      });
    } catch (err) {
      console.error('[PaypercutCheckout] Failed to submit payment:', err);
      throw err;
    }
  }

  /**
   * Send message to hosted/embedded checkout iframe - used for submit payment and communicating 3DS events
   */
  private postToIframe(message: any): void {
    if (!this.iframe?.contentWindow) {
      console.error('[PaypercutCheckout] Cannot post message: iframe not mounted');
      return;
    }

    const checkoutOrigin = getCheckoutOrigin(this.options.hostedCheckoutUrl);
    this.iframe.contentWindow.postMessage(message, checkoutOrigin);
  }

  /**
   * Handle CHECKOUT_RESIZE message - resize iframe to match content height
   */
  private handleResize(height: unknown): void {
    if (!this.iframe || typeof height !== 'number' || height <= 0) {
      return;
    }

    // Set iframe height to match content
    this.iframe.style.height = `${height}px`;
  }

  private buildThreeDSUrl(data: ThreeDSFlowData): string {
    const checkoutOrigin = getCheckoutOrigin(this.options.hostedCheckoutUrl);

    // Build URL: baseUrl/c/{checkoutId}/threeds with query params
    const threeDSUrl = new URL(`/c/${this.options.id}/threeds`, checkoutOrigin);
    threeDSUrl.searchParams.set('sessionId', data.sessionId);
    threeDSUrl.searchParams.set('step', data.step);

    if (data.challengeUrl) {
      threeDSUrl.searchParams.set('challengeUrl', data.challengeUrl);
    }
    if (data.acsTransactionId) {
      threeDSUrl.searchParams.set('acsTransactionId', data.acsTransactionId);
    }
    if (data.threeDSVersion) {
      threeDSUrl.searchParams.set('threeDSVersion', data.threeDSVersion);
    }
    // Always include liveMode; default to false when undefined
    threeDSUrl.searchParams.set('liveMode', String(data.liveMode ?? false));

    return threeDSUrl.toString();
  }

  private handle3DSShellCancel(): void {
    this.postToIframe({
      type: 'THREEDS_CANCELED',
      checkoutId: this.options.id,
      reason: 'sdk_shell_cancel',
    });
    this.threeDSModal.close();
    this.emitter.emit(SdkEvent.ThreeDSCanceled);
  }

  /**
   * Handle THREEDS_READY message - send THREEDS_INIT with challenge data
   */
  private handle3DSReady(): void {
    if (!this.threeDSModal.handleReady()) {
      console.error('[PaypercutCheckout] No pending 3DS data or iframe not ready');
    }
  }

  private postThreeDSInit(iframe: HTMLIFrameElement): void {
    const checkoutOrigin = getCheckoutOrigin(this.options.hostedCheckoutUrl);
    iframe.contentWindow?.postMessage(
      {
        type: 'THREEDS_INIT',
        checkoutId: this.options.id,
      },
      checkoutOrigin,
    );
  }

  // ---------------------------------------------------------------------------
  // Apple Pay bridge
  // ---------------------------------------------------------------------------

  /**
   * Called when the embedded iframe requests that we create an ApplePaySession at the
   * top-level window so the payment sheet / QR popup is not clipped by the container.
   */
  private handleApplePayStart(data: { checkoutId: string; request: object }): void {
    this.abortApplePaySession();

    const ApplePayAPI = (window as any).ApplePaySession;
    if (!ApplePayAPI) {
      this.postToIframe({ type: ApplePayBridgeMsg.SESSION_ERROR, checkoutId: this.options.id });
      return;
    }

    const version = selectApplePaySessionVersion(ApplePayAPI);
    let session: any;
    try {
      session = new ApplePayAPI(version, data.request);
    } catch (e: any) {
      console.error(
        e?.name === 'SecurityError'
          ? '[PaypercutCheckout] Apple Pay blocked — parent page missing allow="payment" on iframe'
          : '[PaypercutCheckout] Failed to create ApplePaySession',
        e,
      );
      this.postToIframe({ type: ApplePayBridgeMsg.SESSION_ERROR, checkoutId: this.options.id });
      return;
    }

    this.applePaySession = session;

    session.onvalidatemerchant = (event: any) => {
      // If the iframe doesn't respond within 15 s, abort — Apple's own timeout is ~30 s
      // and we need to clean up the ref before that fires.
      this.applePayBridgeTimeout = setTimeout(
        () => this.abortApplePaySession(),
        APPLE_PAY_BRIDGE_POLICY.merchantValidationTimeoutMs,
      );
      this.postToIframe({
        type: ApplePayBridgeMsg.VALIDATE_MERCHANT,
        checkoutId: this.options.id,
        validationURL: event.validationURL,
      });
    };

    session.onpaymentauthorized = (event: any) => {
      this.postToIframe({
        type: ApplePayBridgeMsg.PAYMENT_DATA,
        checkoutId: this.options.id,
        payment: event.payment,
      });
    };

    session.oncancel = () => {
      this.applePaySession = null;
      this.postToIframe({ type: ApplePayBridgeMsg.CANCELLED, checkoutId: this.options.id });
    };

    session.begin();
  }

  private handleApplePayMerchantSession(merchantSession: unknown): void {
    if (this.applePayBridgeTimeout) {
      clearTimeout(this.applePayBridgeTimeout);
      this.applePayBridgeTimeout = null;
    }
    if (!this.applePaySession) return;
    try {
      this.applePaySession.completeMerchantValidation(merchantSession);
    } catch (e) {
      console.error('[PaypercutCheckout] completeMerchantValidation failed', e);
      this.abortApplePaySession();
      this.postToIframe({ type: ApplePayBridgeMsg.SESSION_ERROR, checkoutId: this.options.id });
    }
  }

  private handleApplePayPaymentResult(success: boolean): void {
    if (!this.applePaySession) return;
    const ApplePayAPI = (window as any).ApplePaySession;
    try {
      this.applePaySession.completePayment(
        success ? ApplePayAPI.STATUS_SUCCESS : ApplePayAPI.STATUS_FAILURE,
      );
    } catch (e) {
      console.error('[PaypercutCheckout] completePayment failed', e);
    }
    this.applePaySession = null;
  }

  private abortApplePaySession(): void {
    if (this.applePayBridgeTimeout) {
      clearTimeout(this.applePayBridgeTimeout);
      this.applePayBridgeTimeout = null;
    }
    try {
      this.applePaySession?.abort();
    } catch {}
    this.applePaySession = null;
  }

  /**
   * Destroy the checkout instance and cleanup (idempotent)
   */
  destroy(): void {
    // Early return if already destroyed
    if (this.destroyed) {
      return;
    }

    // Remove iframe from DOM
    if (this.iframe) {
      try {
        this.iframe.remove();
        this.iframe = null;
      } catch (err) {
        console.error('[PaypercutCheckout] Error removing iframe:', err);
      }
    }

    // Cleanup 3DS modal if open
    this.threeDSModal.close();

    // Abort Apple Pay session if active
    this.abortApplePaySession();

    // Only set mounted = false after successful DOM removal
    this.mounted = false;
    this.destroyed = true;

    // Cleanup event listeners
    window.removeEventListener('message', this.messageHandler);
    this.emitter.clear();
  }

  /**
   * Subscribe to an event
   */
  on(event: EventName, handler: (...args: any[]) => void): () => void {
    return this.emitter.on(event, handler);
  }

  /**
   * Subscribe to event that auto-unsubscribes after first emission
   */
  once(event: EventName, handler: (...args: any[]) => void): () => void {
    return this.emitter.once(event, handler);
  }

  /**
   * Unsubscribe from an event
   */
  off(event: EventName, handler: (...args: any[]) => void): void {
    this.emitter.off(event, handler);
  }

  /**
   * Check if checkout is currently mounted
   */
  isMounted(): boolean {
    return this.mounted;
  }

  /**
   * Complete form validation successfully - allows wallet payment to proceed.
   * Call this in response to 'form_validation' event when merchant form is valid.
   */
  completeFormValidation(wallet: WalletType): void {
    if (!this.mounted) {
      console.warn('[PaypercutCheckout] Cannot complete form validation: not mounted');
      return;
    }

    this.postToIframe({
      type: 'FORM_VALIDATION_COMPLETED',
      checkoutId: this.options.id,
      wallet,
      status: 'success',
    });
  }

  /**
   * Dynamically update the checkout appearance after it has been rendered.
   */
  updateAppearance(appearance: Appearance): void {
    if (!this.mounted) {
      console.warn('[PaypercutCheckout] Cannot update appearance: not mounted');
      return;
    }
    warnInvalidAppearanceVariables(appearance.variables);
    this.postToIframe({
      type: 'UPDATE_APPEARANCE',
      checkoutId: this.options.id,
      appearance,
    });
  }

  /**
   * Fail form validation - prevents wallet payment from starting.
   * Call this in response to 'form_validation' event when merchant form has errors.
   */
  failFormValidation(wallet: WalletType, errors?: FormValidationError[]): void {
    if (!this.mounted) {
      console.warn('[PaypercutCheckout] Cannot fail form validation: not mounted');
      return;
    }

    this.postToIframe({
      type: 'FORM_VALIDATION_COMPLETED',
      checkoutId: this.options.id,
      wallet,
      status: 'failed',
      errors,
    });
  }
}

/**
 * Factory function that works both as callable and constructable.
 * Always returns a new CheckoutImpl instance - no prototype manipulation needed.
 */
const PaypercutCheckout: PaypercutCheckoutStatic = function (
  this: unknown,
  options: PaypercutCheckoutOptions,
): CheckoutInstance {
  // Always return a new instance, regardless of how it's called
  return new CheckoutImpl(options);
} as unknown as PaypercutCheckoutStatic;

// Add static version property
(PaypercutCheckout as any).version = config.version;

// Export as default and named
export default PaypercutCheckout;
export { PaypercutCheckout };
